import React from 'react';

/**
 * React Error Boundary 컴포넌트
 * 하위 컴포넌트에서 발생하는 JavaScript 에러를 잡아 처리합니다.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // 다음 렌더링에서 fallback UI가 보이도록 상태를 업데이트합니다.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // 에러 로깅
    console.error('[ErrorBoundary] 에러 발생:', error);
    console.error('[ErrorBoundary] 컴포넌트 스택:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // 프로덕션에서는 에러 리포팅 서비스에 전송할 수 있습니다.
    // 예: Sentry, LogRocket 등
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // 사용자 정의 fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="surface p-6 max-w-md w-full text-center space-y-4">
            <div className="text-4xl">😵</div>
            <h2 className="text-xl font-display font-semibold text-ink-900 dark:text-ink-50">
              문제가 발생했습니다
            </h2>
            <p className="text-sm text-ink-600 dark:text-ink-300 font-sans">
              {this.props.message || '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'}
            </p>

            {/* 개발 환경에서만 에러 상세 정보 표시 */}
            {import.meta.env.DEV && this.state.error && (
              <details className="text-left mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <summary className="text-sm font-medium text-red-800 dark:text-red-200 cursor-pointer">
                  에러 상세 정보
                </summary>
                <pre className="mt-2 text-xs text-red-600 dark:text-red-300 whitespace-pre-wrap overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center pt-2">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 text-sm font-medium rounded-xl border border-ink-200 dark:border-ink-800 text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-900 transition-all duration-300"
              >
                다시 시도
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 py-2 text-sm font-medium rounded-xl bg-ink-900 dark:bg-ink-50 text-white dark:text-ink-950 transition-all duration-300"
              >
                페이지 새로고침
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
